import { Mail, Phone } from "lucide-react";

import type adminAPI from "@/services/api";

export type PnrContact = Awaited<ReturnType<typeof adminAPI.retrievePnrContacts>>[number];

interface PnrContactsProps {
  /** Already one entry per distinct email or phone — the API merges and cleans the PNR lines. */
  contacts: PnrContact[];
  /** Lower-cased passenger email → family name, to tell which PNR address is already in use. */
  familyByEmail: ReadonlyMap<string, string>;
}

/** « Contacts du PNR » — every address and phone the booking holds, read before the cessions are generated. */
export function PnrContacts({ contacts, familyByEmail }: PnrContactsProps) {
  if (contacts.length === 0) {
    return null;
  }

  return (
    <section className="mt-5 flex flex-col gap-2">
      <h4 className="flex items-center gap-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
        Contacts du PNR
        <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium tracking-normal text-gray-600 normal-case tabular-nums">
          {contacts.length}
        </span>
      </h4>
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
        {contacts.map((contact) => {
          const Icon = contact.kind === "email" ? Mail : Phone;
          const family = contact.kind === "email" ? familyByEmail.get(contact.value) : undefined;
          const details = [contact.holder, contact.passengerType].filter(Boolean).join(" · ");

          return (
            <li key={`${contact.kind}-${contact.value}`} className="flex items-start gap-2.5 px-3 py-2.5">
              <Icon className="mt-0.5 size-4 shrink-0 text-gray-400" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm break-all text-gray-900 tabular-nums">{contact.value}</p>
                {details || family ? (
                  <p className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-gray-500">
                    {details ? <span>{details}</span> : null}
                    {family ? <span className="text-emerald-700">Contact de la famille {family}</span> : null}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
